import type { Category, CategoryBanner } from "../types/category.dto";

/**
 * As quatro artes de uma categoria, cadastradas no portal e devolvidas dentro de
 * `category.banners` (linhas de `ecm__category_banner`, uma por `type`).
 *
 * São dois eixos:
 *  - LUGAR — `panel` é a arte RETRATO do painel de departamentos; `banner` é a
 *            PAISAGEM da barra lateral da listagem. Recortes diferentes, não a
 *            mesma foto em dois tamanhos.
 *  - TELA  — `mobile` e `desktop`.
 *
 * Antes destes tipos existirem tudo saía de `category.image`, e a mesma foto ia
 * para os dois lugares: deitada na barra lateral, esticada pelo `object-cover`
 * do painel. `image` continua valendo como MINIATURA (o thumb ao lado do nome
 * nas listas) e como último fallback.
 */
const TYPES = {
    panelMobile: "panel_mobile",
    panelDesktop: "panel_desktop",
    // Vídeo curto do painel: tipos próprios para que uma versão antiga da loja/app
    // (que só conhece `panel_*`) ignore o `.mp4` em vez de mandá-lo ao next/image.
    panelVideoMobile: "panel_video_mobile",
    panelVideoDesktop: "panel_video_desktop",
    bannerMobile: "banner_mobile",
    bannerDesktop: "banner_desktop",
    panelProducts: "panel_products",
} as const;

export interface CategoryArtwork {
    panelMobile?: string;
    panelDesktop?: string;
    panelVideoMobile?: string;
    panelVideoDesktop?: string;
    bannerMobile?: string;
    bannerDesktop?: string;
    panelProducts?: number[];
}

/**
 * `type` é varchar livre no banco e a API devolve o que estiver gravado — o DTO
 * legado ainda documenta "TOP"/"MIDDLE"/"BOTTOM" em maiúsculas. Normalizar aqui
 * evita que uma linha gravada como `PANEL_DESKTOP` por qualquer outro caminho
 * simplesmente não case.
 */
function typeOf(banner: CategoryBanner): string {
    return String(banner.type ?? "").trim().toLowerCase();
}

function imageOf(banners: CategoryBanner[] | undefined, type: string): string | undefined {
    const match = banners?.find((banner) => typeOf(banner) === type && banner.status !== 0);
    return match?.image || undefined;
}

function productIdsOf(banners: CategoryBanner[] | undefined): number[] | undefined {
    const match = banners?.find((banner) => typeOf(banner) === TYPES.panelProducts && banner.status !== 0);
    const ids = Array.isArray(match?.items)
        ? (match.items as unknown[]).filter((id): id is number => typeof id === "number")
        : [];
    return ids.length > 0 ? ids : undefined;
}

/** As artes da categoria. Campos sem linha cadastrada saem `undefined`. */
export function categoryArtwork(category: Pick<Category, "banners">): CategoryArtwork {
    const banners = category.banners;
    return {
        panelMobile: imageOf(banners, TYPES.panelMobile),
        panelDesktop: imageOf(banners, TYPES.panelDesktop),
        panelVideoMobile: imageOf(banners, TYPES.panelVideoMobile),
        panelVideoDesktop: imageOf(banners, TYPES.panelVideoDesktop),
        bannerMobile: imageOf(banners, TYPES.bannerMobile),
        bannerDesktop: imageOf(banners, TYPES.bannerDesktop),
        panelProducts: productIdsOf(banners),
    };
}
